import { useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useGetRohsProductComplianceFiles,
  type ComplianceActor,
  type RohsComponentComplianceFileView,
  type RohsProductComplianceFilesState,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { trackedRohsParts } from '@/v2/lib/trackedRohsParts'
import { V2TableLink } from '@/v2/components/V2TableLink'
import {
  getV2ComponentDetailPath,
  getV2ProductPartsPath,
  getV2SupplierDetailPath,
} from '@/v2/utils/routes'

type V2RohsProductComplianceFilesSectionProps = {
  productId: string
  /** RoHS applicability status on this product; slots exist only once RoHS applies. */
  rohsApplies: boolean
  embedded?: boolean
  collapsible?: boolean
}

type PartDutyRow = {
  id: string
  part: RohsComponentComplianceFileView
  owner: ComplianceActor
}

function TaskStatusBadge({ complete }: { complete: boolean }) {
  const { t } = useTranslation('common')

  return (
    <Badge variant={complete ? 'success' : 'secondary'}>
      {complete
        ? t('v2.pages.products.detail.rohsFiles.duties.table.statusComplete')
        : t('v2.pages.products.detail.rohsFiles.duties.table.statusOpen')}
    </Badge>
  )
}

function OwnerBadge({ actor }: { actor: ComplianceActor }) {
  const { t } = useTranslation('common')

  return (
    <Badge variant='outline'>
      {t(`v2.pages.products.detail.rohsFiles.responsibility.${actor.toLowerCase()}`)}
    </Badge>
  )
}

function buildPartDutyRows(
  files: RohsProductComplianceFilesState
): PartDutyRow[] {
  return trackedRohsParts(files.components).map((part) => ({
    id: `part-${part.componentId}`,
    part,
    owner: part.complianceActor,
  }))
}

function sortPartDutyRows(rows: PartDutyRow[], locale: string): PartDutyRow[] {
  const compareSku = (left: PartDutyRow, right: PartDutyRow): number => {
    return left.part.sku
      .trim()
      .localeCompare(right.part.sku.trim(), locale, { sensitivity: 'base' })
  }

  const openRows = rows
    .filter((row) => row.part.completedAt == null)
    .sort(compareSku)
  const completeRows = rows
    .filter((row) => row.part.completedAt != null)
    .sort(compareSku)

  return [...openRows, ...completeRows]
}

function RohsPartEvidenceTable({
  files,
  productId,
}: {
  files: RohsProductComplianceFilesState
  productId: string
}) {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const partRows = useMemo(() => buildPartDutyRows(files), [files])
  const sortedPartRows = useMemo(
    () => sortPartDutyRows(partRows, i18n.language),
    [partRows, i18n.language]
  )

  const openComponentFile = (componentId: string) => {
    void navigate(getV2ComponentDetailPath(componentId))
  }

  if (files.components.length === 0) {
    return (
      <div className='space-y-2'>
        <p className='text-sm text-muted-foreground'>
          {t('v2.pages.products.detail.rohsFiles.duties.table.noParts')}
        </p>
        <div className='flex justify-end'>
          <Button asChild size='sm' variant='outline'>
            <Link to={getV2ProductPartsPath(productId)}>
              {t('v2.pages.products.detail.parts.viewAllButton')}
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  if (sortedPartRows.length === 0) {
    return null
  }

  return (
    <Table className='table-fixed'>
      <colgroup>
        <col className='w-[34%]' />
        <col className='w-[12%]' />
        <col className='w-[16%]' />
        <col className='w-[16%]' />
        <col className='w-[140px]' />
      </colgroup>
      <TableHeader>
        <TableRow>
          <TableHead className='w-[34%]'>
            {t('v2.pages.products.detail.rohsFiles.duties.table.part')}
          </TableHead>
          <TableHead className='w-[12%]'>
            {t('v2.pages.products.detail.rohsFiles.duties.table.owner')}
          </TableHead>
          <TableHead className='w-[16%]'>
            {t('v2.pages.products.detail.rohsFiles.duties.table.supplier')}
          </TableHead>
          <TableHead className='w-[16%]'>
            {t('v2.pages.products.detail.rohsFiles.duties.table.status')}
          </TableHead>
          <TableHead className='w-[140px] text-end'>
            {t('v2.pages.products.detail.rohsFiles.duties.table.actions')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPartRows.map((row) => {
          const partName =
            row.part.componentName != null && row.part.componentName.trim() !== ''
              ? row.part.componentName.trim()
              : t('v2.pages.components.fallbackName')
          const partComplete = row.part.completedAt != null
          const missingComplianceFile = row.part.id == null
          let actionLabel = t(
            'v2.pages.products.detail.rohsFiles.duties.table.supplyEvidence'
          )
          if (partComplete) {
            actionLabel = t(
              'v2.pages.products.detail.rohsFiles.duties.table.viewFile'
            )
          }

          return (
            <TableRow key={row.id}>
              <TableCell className='whitespace-normal'>
                <div className='truncate text-sm font-medium' title={row.part.sku}>
                  {row.part.sku}
                </div>
                <div
                  className='truncate text-xs text-muted-foreground'
                  title={partName}
                >
                  {partName}
                </div>
              </TableCell>
              <TableCell>
                <OwnerBadge actor={row.owner} />
              </TableCell>
              <TableCell className='max-w-0'>
                <V2TableLink
                  to={getV2SupplierDetailPath(row.part.supplierId)}
                  truncate
                  title={row.part.supplierName}
                >
                  {row.part.supplierName}
                </V2TableLink>
              </TableCell>
              <TableCell>
                <div className='flex flex-col items-start gap-1'>
                  <TaskStatusBadge complete={partComplete} />
                  {missingComplianceFile ? (
                    <span className='text-xs text-muted-foreground'>
                      {t(
                        'v2.pages.products.detail.rohsFiles.duties.table.missingComplianceFile'
                      )}
                    </span>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className='text-end'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    openComponentFile(row.part.componentId)
                  }}
                >
                  {actionLabel}
                </Button>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function CollapsiblePartList({
  summary,
  defaultOpen,
  children,
}: {
  summary: string
  defaultOpen: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <Collapsible open={open} onOpenChange={setOpen} className='rounded-md border'>
      <CollapsibleTrigger className='flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left outline-none hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring'>
        <span className='text-sm font-medium'>{summary}</span>
        <ChevronDown
          className='size-4 shrink-0 text-muted-foreground transition-transform duration-200 in-data-panel-open:rotate-180'
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className='border-t px-3 py-2'>{children}</CollapsibleContent>
    </Collapsible>
  )
}

export function V2RohsProductComplianceFilesSection({
  productId,
  rohsApplies,
  embedded = false,
  collapsible = false,
}: V2RohsProductComplianceFilesSectionProps) {
  const { t } = useTranslation('common')
  const { data: files, isLoading } = useGetRohsProductComplianceFiles(productId, {
    enabled: rohsApplies,
  })

  if (!rohsApplies) {
    return null
  }

  let content: ReactNode
  if (isLoading) {
    content = (
      <div className='space-y-2' aria-busy='true'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
      </div>
    )
  } else if (files != null) {
    const partRows = buildPartDutyRows(files)
    const completeCount = partRows.filter(
      (row) => row.part.completedAt != null
    ).length
    const openCount = partRows.length - completeCount
    const showCollapsiblePartList =
      collapsible && files.components.length > 0 && partRows.length > 0

    if (showCollapsiblePartList) {
      const summary = t(
        'v2.pages.products.detail.rohsFiles.duties.componentEvidenceSummary',
        {
          count: partRows.length,
          complete: completeCount,
          open: openCount,
        }
      )
      content = (
        <CollapsiblePartList summary={summary} defaultOpen={openCount > 0}>
          <RohsPartEvidenceTable files={files} productId={productId} />
        </CollapsiblePartList>
      )
    } else {
      content = <RohsPartEvidenceTable files={files} productId={productId} />
    }
  } else {
    content = (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.rohsFiles.loadFailed')}
      </p>
    )
  }

  if (embedded) {
    return <div data-testid='v2-rohs-compliance-files'>{content}</div>
  }

  return (
    <section className='space-y-3' data-testid='v2-rohs-compliance-files'>
      {content}
    </section>
  )
}
